import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { Enterprise, Identity } from '@/types/enterprise'
import { IdentityTypeEnum } from '@/types/enterprise'
import { getEnterpriseList, switchEnterprise, switchToPersonalApi } from '@/api/enterprise'
import { clearSealToken } from '@/utils/sealTokenManager'
import { getQuotaBalance } from '@/api/member/quota'

export const useEnterpriseStore = defineStore('enterprise', () => {
  // localStorage key（仅作为页面刷新时的快速缓存，后端是真正的数据源）
  const STORAGE_KEY = 'portal_current_enterprise_id'
  const IDENTITY_STORAGE_KEY = 'portal_current_identity'
  
  // ✅ 从 localStorage 读取缓存的身份信息（用于页面刷新时快速渲染）
  const initCurrentIdentity = (): Identity | null => {
    try {
      const savedIdentity = localStorage.getItem(IDENTITY_STORAGE_KEY)
      if (savedIdentity) {
        const identity = JSON.parse(savedIdentity) as Identity
        console.log('[Enterprise Store] 从 localStorage 恢复身份缓存:', identity.type)
        return identity
      }
    } catch (error) {
      console.error('[Enterprise Store] 读取身份缓存失败:', error)
    }
    return null
  }
  
  // ✅ 从 localStorage 读取缓存的企业信息
  const initCurrentEnterprise = (): Enterprise | null => {
    try {
      const savedEnterpriseData = localStorage.getItem('portal_current_enterprise_data')
      if (savedEnterpriseData) {
        const enterpriseData = JSON.parse(savedEnterpriseData)
        console.log('[Enterprise Store] 从 localStorage 恢复企业缓存:', enterpriseData.name)
        return enterpriseData
      }
    } catch (error) {
      console.error('[Enterprise Store] 读取企业缓存失败:', error)
    }
    return null
  }
  
  // ✅ 当前身份（个人/企业）
  const currentIdentity = ref<Identity | null>(initCurrentIdentity())
  const currentEnterprise = ref<Enterprise | null>(initCurrentEnterprise())
  const enterpriseList = ref<Enterprise[]>([])
  
  // 🔧 配额功能是否启用（全局共享，避免多组件重复调用 balance API）
  const quotaEnabled = ref(true)
  
  // 🔧 当前身份的配额余额缓存（null = 尚未加载）
  const currentQuotaBalance = ref<number | null>(null)
  
  // 🔧 去重：共享的配额检查 Promise，确保并发调用只触发一次 API 请求
  let _quotaPromise: Promise<{ enabled: boolean; balance: number }> | null = null
  
  /**
   * 确保当前身份的配额已加载（去重机制）
   * 多个组件同时调用此方法，只会产生一次实际的 API 请求。
   * 返回 { enabled, balance } 供调用方判断。
   */
  const ensureCurrentQuotaLoaded = (): Promise<{ enabled: boolean; balance: number }> => {
    // 如果已有缓存结果，直接返回
    if (currentQuotaBalance.value !== null) {
      return Promise.resolve({ enabled: quotaEnabled.value, balance: currentQuotaBalance.value })
    }
    
    // 如果已有进行中的请求，复用同一个 Promise
    if (_quotaPromise) {
      return _quotaPromise
    }
    
    // 发起新的请求
    const enterpriseId = isPersonalIdentity.value ? undefined : currentEnterprise.value?.id
    _quotaPromise = getQuotaBalance(enterpriseId)
      .then((data: any) => {
        if (data?.enabled === false) {
          quotaEnabled.value = false
          currentQuotaBalance.value = 0
          return { enabled: false, balance: 0 }
        }
        const balance = data?.balance ?? 0
        quotaEnabled.value = true
        currentQuotaBalance.value = balance
        return { enabled: true, balance }
      })
      .catch((err: any) => {
        console.warn('[Enterprise Store] 配额检查失败:', err)
        // 失败时允许继续，让后端做最终校验
        return { enabled: true, balance: -1 }
      })
      .finally(() => {
        _quotaPromise = null // 完成后清除，允许后续刷新
      })
    
    return _quotaPromise
  }
  
  /**
   * 重置配额缓存（身份切换或支付成功后调用）
   */
  const resetQuotaCache = () => {
    currentQuotaBalance.value = null
    _quotaPromise = null
  }
  
  // ✅ 是否为个人身份
  const isPersonalIdentity = computed(() => {
    return currentIdentity.value?.type === IdentityTypeEnum.PERSONAL
  })
  
  // ✅ 是否为企业身份
  const isEnterpriseIdentity = computed(() => {
    return currentIdentity.value?.type === IdentityTypeEnum.ENTERPRISE
  })
  
  // 🔧 角色文本映射
  const getRoleText = (role?: number): string => {
    switch (role) {
      case 1: return '所有者'
      case 2: return '管理员'
      case 3: return '成员'
      default: return '成员'
    }
  }
  
  // 🔧 保存身份信息到 localStorage（作为快速缓存）
  const saveIdentityToCache = (identity: Identity, enterprise: Enterprise | null) => {
    localStorage.setItem(IDENTITY_STORAGE_KEY, JSON.stringify(identity))
    if (enterprise) {
      localStorage.setItem(STORAGE_KEY, String(enterprise.id))
      localStorage.setItem('portal_current_enterprise_data', JSON.stringify(enterprise))
    } else {
      localStorage.removeItem(STORAGE_KEY)
      localStorage.removeItem('portal_current_enterprise_data')
    }
  }
  
  /**
   * ✅ 获取企业列表并初始化身份（后端驱动）
   * 
   * 核心设计：后端 current_enterprise_id 字段是身份的数据源：
   *   - NULL → 个人模式（返回 activeEnterprise = null）
   *   - 有值 → 企业模式（返回 activeEnterprise = {...}）
   * 
   * @param userInfo 当前用户信息，用于构建个人身份
   */
  const fetchEnterpriseList = async (userInfo?: { id: number; nickname: string; verifyStatus?: number }) => {
    try {
      const res = await getEnterpriseList()
      console.log('[Enterprise Store] API 返回数据:', res)
      
      const result = (res as any).data
      enterpriseList.value = result?.list || []
      
      console.log('[Enterprise Store] 企业列表:', enterpriseList.value.length, '个企业')
      
      // ✅ 后端驱动身份判断：activeEnterprise 有值 = 企业模式，null = 个人模式
      const activeEnterprise = result?.activeEnterprise
      
      if (activeEnterprise) {
        // 企业模式 - 后端确认用户上次选择了此企业
        currentEnterprise.value = activeEnterprise
        const enterpriseIdentity: Identity = {
          type: IdentityTypeEnum.ENTERPRISE,
          id: activeEnterprise.id,
          name: activeEnterprise.name,
          verified: activeEnterprise.status === 2,
          verifyStatus: activeEnterprise.status,
          role: getRoleText(activeEnterprise.role),
          roleCode: activeEnterprise.role
        }
        currentIdentity.value = enterpriseIdentity
        saveIdentityToCache(enterpriseIdentity, activeEnterprise)
        console.log('[Enterprise Store] 后端确认企业身份:', activeEnterprise.name)
      } else if (userInfo) {
        // 个人模式 - 后端 current_enterprise_id 为 NULL
        currentEnterprise.value = null
        const personalIdentity: Identity = {
          type: IdentityTypeEnum.PERSONAL,
          id: userInfo.id,
          name: '个人',
          verified: userInfo.verifyStatus === 2,
          verifyStatus: userInfo.verifyStatus
        }
        currentIdentity.value = personalIdentity
        saveIdentityToCache(personalIdentity, null)
        console.log('[Enterprise Store] 后端确认个人身份')
      }
      
      return res
    } catch (error) {
      console.error('[Enterprise Store] 获取企业列表失败:', error)
      enterpriseList.value = []
      currentEnterprise.value = null
      throw error
    }
  }

  const switchCurrentEnterprise = async (enterpriseId: number) => {
    await switchEnterprise(enterpriseId)
    const target = enterpriseList.value.find((e) => e.id === enterpriseId)
    if (target) {
      currentEnterprise.value = target
      const enterpriseIdentity: Identity = {
        type: IdentityTypeEnum.ENTERPRISE,
        id: target.id,
        name: target.name,
        verified: target.status === 2,
        verifyStatus: target.status,
        role: getRoleText(target.role),
        roleCode: target.role
      }
      currentIdentity.value = enterpriseIdentity
      saveIdentityToCache(enterpriseIdentity, target)
      console.log('[Enterprise Store] 企业切换成功:', target.name)
      // 刷新页面以重新加载数据
      window.location.reload()
    }
  }

  const clearCurrentEnterprise = () => {
    currentEnterprise.value = null
    enterpriseList.value = []
    localStorage.removeItem(STORAGE_KEY)
    localStorage.removeItem('portal_current_enterprise_data')
    console.log('[Enterprise Store] 已清除企业信息')
  }
  
  // ✅ 切换到个人身份（调用后端 API）
  const switchToPersonal = async (userInfo: { id: number; nickname: string; verifyStatus?: number }) => {
    // 先调用后端 API 清除 current_enterprise_id
    await switchToPersonalApi()
    
    const personalIdentity: Identity = {
      type: IdentityTypeEnum.PERSONAL,
      id: userInfo.id,
      name: '个人',
      verified: userInfo.verifyStatus === 2,
      verifyStatus: userInfo.verifyStatus
    }
    
    currentIdentity.value = personalIdentity
    currentEnterprise.value = null
    saveIdentityToCache(personalIdentity, null)
    
    // 清除 Seal Token 缓存（旧 Token 中嵌入了 enterpriseId）
    clearSealToken()
    // 重置配额缓存
    resetQuotaCache()
    
    console.log('[Enterprise Store] 切换到个人身份（已同步后端）')
  }
  
  // ✅ 切换到企业身份（调用后端 API）
  const switchToEnterprise = async (enterprise: Enterprise) => {
    // 先调用后端切换 API（更新 current_enterprise_id）
    await switchEnterprise(enterprise.id)
    
    const enterpriseIdentity: Identity = {
      type: IdentityTypeEnum.ENTERPRISE,
      id: enterprise.id,
      name: enterprise.name,
      verified: enterprise.status === 2,
      verifyStatus: enterprise.status,
      role: getRoleText(enterprise.role),
      roleCode: enterprise.role
    }
    
    currentIdentity.value = enterpriseIdentity
    currentEnterprise.value = enterprise
    saveIdentityToCache(enterpriseIdentity, enterprise)
    
    // 清除 Seal Token 缓存
    clearSealToken()
    // 重置配额缓存
    resetQuotaCache()
    
    console.log('[Enterprise Store] 切换到企业身份（已同步后端）:', enterprise.name)
  }
  
  // ✅ 清除所有身份和企业信息（用于 401 或异常场景）
  const clearIdentityAndEnterprise = () => {
    currentIdentity.value = null
    currentEnterprise.value = null
    enterpriseList.value = []
    localStorage.removeItem(IDENTITY_STORAGE_KEY)
    localStorage.removeItem(STORAGE_KEY)
    localStorage.removeItem('portal_current_enterprise_data')
    clearSealToken()
    resetQuotaCache()
    console.log('[Enterprise Store] 已清除所有身份和企业信息')
  }

  return {
    // 状态
    currentIdentity,
    currentEnterprise,
    enterpriseList,
    quotaEnabled,
    currentQuotaBalance,
    // 计算属性
    isPersonalIdentity,
    isEnterpriseIdentity,
    // 方法
    fetchEnterpriseList,
    switchCurrentEnterprise,
    clearCurrentEnterprise,
    switchToPersonal,
    switchToEnterprise,
    clearIdentityAndEnterprise,
    ensureCurrentQuotaLoaded,
    resetQuotaCache,
  }
})
